import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, BookOpen, Eye, ThumbsUp, ThumbsDown, Loader2, Plus, Pencil, Trash2, MessageCircle } from "lucide-react";
import { EmptyState } from "@/components/states/EmptyState";
import { LoadingSkeleton } from "@/components/states/LoadingSkeleton";
import { ErrorState } from "@/components/states/ErrorState";
import { motion } from "framer-motion";
import { toast } from "sonner";

type Article = {
  id: string;
  title: string;
  content: string;
  category: string | null;
  tags: any;
  status: "draft" | "published";
  views: number;
  helpful_count: number;
  not_helpful_count: number;
  source: string;
  source_sender_name: string | null;
  created_at: string;
  slug: string | null;
};

export default function KnowledgeBase() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<"all" | "published" | "draft">("published");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[] | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Article | null>(null);
  const [form, setForm] = useState({ title: "", content: "", category: "", tags: "", status: "published" as "draft" | "published" });
  const [saving, setSaving] = useState(false);

  const { data: orgMembership } = useQuery({
    queryKey: ["my-org-membership", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data } = await supabase.from("org_members").select("org_id").eq("user_id", user!.id).limit(1).maybeSingle();
      return data;
    },
  });
  const orgId = orgMembership?.org_id;

  const { data: isAdmin = false } = useQuery({
    queryKey: ["is-admin", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data } = await supabase.from("user_roles").select("role").eq("user_id", user!.id).eq("role", "admin").maybeSingle();
      return !!data;
    },
  });

  const { data: articles = [], isLoading, error, refetch } = useQuery({
    queryKey: ["kb-articles-public", orgId, statusFilter],
    enabled: !!orgId,
    queryFn: async () => {
      let q = supabase
        .from("kb_articles")
        .select("id, title, content, category, views, helpful_count, not_helpful_count, created_at, tags, slug, status, source, source_sender_name")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false });
      if (statusFilter !== "all") q = q.eq("status", statusFilter);
      const { data, error } = await q;
      if (error) throw error;
      return data as Article[];
    },
  });

  const categories = [...new Set(articles.map((a) => a.category).filter(Boolean) as string[])];

  const handleSearch = async () => {
    if (!search.trim() || !orgId) return;
    setSearching(true);
    try {
      const { data, error } = await supabase.functions.invoke("kb-search", {
        body: { query: search.trim(), org_id: orgId, limit: 10 },
      });
      if (error) throw error;
      setSearchResults(data?.articles || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ title: "", content: "", category: "", tags: "", status: "published" });
    setEditorOpen(true);
  };

  const openEdit = (a: Article) => {
    setEditing(a);
    setForm({
      title: a.title,
      content: a.content,
      category: a.category || "",
      tags: Array.isArray(a.tags) ? a.tags.join(", ") : "",
      status: a.status,
    });
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Título e conteúdo são obrigatórios");
      return;
    }
    setSaving(true);
    try {
      const tags = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
      const payload = {
        action: editing ? "update" : "create",
        ...(editing && { id: editing.id }),
        title: form.title.trim(),
        content: form.content.trim(),
        category: form.category.trim() || null,
        tags,
        status: form.status,
        org_id: orgId,
      };
      const { data, error } = await supabase.functions.invoke("kb-article-upsert", { body: payload });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      toast.success(editing ? "Artigo atualizado" : "Artigo criado");
      setEditorOpen(false);
      qc.invalidateQueries({ queryKey: ["kb-articles-public"] });
    } catch (e: any) {
      toast.error(`Erro: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Excluir este artigo?")) return;
    const { data, error } = await supabase.functions.invoke("kb-article-upsert", { body: { action: "delete", id, org_id: orgId } });
    if (error || data?.error) {
      toast.error(`Erro: ${data?.error || error?.message}`);
      return;
    }
    toast.success("Artigo excluído");
    qc.invalidateQueries({ queryKey: ["kb-articles-public"] });
  };

  const handleTogglePublish = async (a: Article) => {
    const action = a.status === "published" ? "unpublish" : "publish";
    const { data, error } = await supabase.functions.invoke("kb-article-upsert", { body: { action, id: a.id, org_id: orgId } });
    if (error || data?.error) {
      toast.error(`Erro: ${data?.error || error?.message}`);
      return;
    }
    toast.success(action === "publish" ? "Publicado" : "Despublicado");
    qc.invalidateQueries({ queryKey: ["kb-articles-public"] });
  };

  const displayArticles = searchResults !== null ? searchResults : (
    selectedCategory ? articles.filter((a) => a.category === selectedCategory) : articles
  );

  if (isLoading) return <div className="p-6"><LoadingSkeleton shape="list" count={5} /></div>;
  if (error) return <div className="p-6"><ErrorState message="Erro ao carregar base de conhecimento" onRetry={refetch} /></div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Base de Conhecimento</h1>
          <p className="text-muted-foreground text-sm">
            {articles.length} artigo(s) {statusFilter !== "all" ? `(${statusFilter === "published" ? "publicados" : "rascunhos"})` : ""}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" /> Novo artigo
          </Button>
        )}
      </div>

      {isAdmin && (
        <Tabs value={statusFilter} onValueChange={(v) => { setStatusFilter(v as any); setSearchResults(null); }}>
          <TabsList>
            <TabsTrigger value="published">Publicados</TabsTrigger>
            <TabsTrigger value="draft">Rascunhos</TabsTrigger>
            <TabsTrigger value="all">Todos</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar artigos..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); if (!e.target.value) setSearchResults(null); }}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="pl-10"
          />
        </div>
        <Button onClick={handleSearch} disabled={searching || !search.trim()}>
          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buscar"}
        </Button>
      </div>

      {/* Categories */}
      {categories.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          <Badge variant={selectedCategory === null ? "default" : "outline"} className="cursor-pointer"
            onClick={() => { setSelectedCategory(null); setSearchResults(null); }}>Todos</Badge>
          {categories.map((cat) => (
            <Badge key={cat} variant={selectedCategory === cat ? "default" : "outline"} className="cursor-pointer"
              onClick={() => { setSelectedCategory(cat); setSearchResults(null); }}>{cat}</Badge>
          ))}
        </div>
      )}

      {searchResults !== null && (
        <p className="text-xs text-muted-foreground">
          {searchResults.length} resultado(s)
          <Button variant="link" className="text-xs p-0 h-auto ml-2" onClick={() => { setSearch(""); setSearchResults(null); }}>Limpar</Button>
        </p>
      )}

      {!isAdmin && (
        <p className="text-xs text-muted-foreground bg-muted/40 rounded-md px-3 py-2">
          💡 Dica: no WhatsApp use <code className="bg-background px-1 rounded">/kb &lt;título&gt;</code> ou reaja com 📚 a uma mensagem para sugerir um artigo.
        </p>
      )}

      {/* Articles */}
      {displayArticles.length === 0 ? (
        <EmptyState icon={BookOpen} title="Nenhum artigo" description={search ? "Tente outros termos" : "Nada por aqui ainda"} />
      ) : (
        <ScrollArea className="h-[calc(100vh-340px)]">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {displayArticles.map((article: any, idx: number) => (
              <motion.div key={article.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}>
                <Card className="hover:border-primary/50 transition-colors h-full flex flex-col">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-sm font-medium leading-tight line-clamp-2">{article.title}</CardTitle>
                      <div className="flex flex-col gap-1 items-end shrink-0">
                        {article.status === "draft" && <Badge variant="secondary" className="text-[10px]">Rascunho</Badge>}
                        {article.category && <Badge variant="outline" className="text-[10px]">{article.category}</Badge>}
                        {article.source === "whatsapp" && (
                          <Badge variant="outline" className="text-[10px] gap-1"><MessageCircle className="h-2.5 w-2.5" />WA</Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3 flex-1 flex flex-col">
                    <p className="text-xs text-muted-foreground line-clamp-3 flex-1">
                      {article.content?.replace(/[#*`]/g, "").slice(0, 200)}
                    </p>
                    {article.source_sender_name && (
                      <p className="text-[10px] text-muted-foreground">via {article.source_sender_name}</p>
                    )}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{article.views || 0}</span>
                        <span className="flex items-center gap-1"><ThumbsUp className="h-3 w-3" />{article.helpful_count || 0}</span>
                        <span className="flex items-center gap-1"><ThumbsDown className="h-3 w-3" />{article.not_helpful_count || 0}</span>
                      </div>
                      {isAdmin && (
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleTogglePublish(article)} title={article.status === "published" ? "Despublicar" : "Publicar"}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(article)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => handleDelete(article.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </ScrollArea>
      )}

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar artigo" : "Novo artigo"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Título</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Como configurar webhook" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoria</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Configuração" />
              </div>
              <div>
                <Label>Tags (separadas por vírgula)</Label>
                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="webhook, setup" />
              </div>
            </div>
            <div>
              <Label>Conteúdo (markdown)</Label>
              <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={10} placeholder="# Título&#10;Conteúdo do artigo..." />
            </div>
            <div className="flex items-center gap-3">
              <Label>Status:</Label>
              <Tabs value={form.status} onValueChange={(v) => setForm({ ...form, status: v as any })}>
                <TabsList>
                  <TabsTrigger value="draft">Rascunho</TabsTrigger>
                  <TabsTrigger value="published">Publicado</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editing ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
